// Schema cache helpers keep track of which optional columns exist so queries stay portable.
import { sql } from './base';

const SCHEMA_CACHE_DURATION = 5 * 60 * 1000; // five minutes

let schemaCache: {
  tasks?: {
    hasTaskType: boolean;
    hasRegular: boolean;
    hasFrequency: boolean;
    hasDaily: boolean;
    hasNextDueDate: boolean;
    hasDependencyColumns: boolean;
    hasSOPColumn: boolean;
    hasNotes: boolean;
    hasRecurrenceDay: boolean;
    hasTimeSpent: boolean;
    hasTimeSpentMinutes: boolean;
    lastChecked: number;
  };
  clients?: {
    hasNotes: boolean;
    hasDescription: boolean;
    hasStatus: boolean;
    hasIsArchived: boolean;
    lastChecked: number;
  };
} = {};

export async function getTasksSchemaInfo() {
  const now = Date.now();
  if (schemaCache.tasks?.lastChecked && (now - schemaCache.tasks.lastChecked) < SCHEMA_CACHE_DURATION) {
    return schemaCache.tasks;
  }

  const columnCheck = await sql`
    SELECT column_name
    FROM information_schema.columns
    WHERE table_name = 'tasks'
      AND column_name IN (
        'task_type', 'is_regular', 'frequency', 'is_daily', 'next_due_date', 'recurrence_day',
        'depends_on', 'is_live', 'dependency_type', 'dependency_group', 'sop', 'notes',
        'time_spent', 'time_spent_minutes'
      )
  `;

  schemaCache.tasks = {
    hasTaskType: columnCheck.some((row: any) => row.column_name === 'task_type'),
    hasRegular: columnCheck.some((row: any) => row.column_name === 'is_regular'),
    hasFrequency: columnCheck.some((row: any) => row.column_name === 'frequency'),
    hasDaily: columnCheck.some((row: any) => row.column_name === 'is_daily'),
    hasNextDueDate: columnCheck.some((row: any) => row.column_name === 'next_due_date'),
    hasDependencyColumns: columnCheck.some((row: any) => row.column_name === 'depends_on'),
    hasSOPColumn: columnCheck.some((row: any) => row.column_name === 'sop'),
    hasNotes: columnCheck.some((row: any) => row.column_name === 'notes'),
    hasRecurrenceDay: columnCheck.some((row: any) => row.column_name === 'recurrence_day'),
    hasTimeSpent: columnCheck.some((row: any) => row.column_name === 'time_spent'),
    hasTimeSpentMinutes: columnCheck.some((row: any) => row.column_name === 'time_spent_minutes'),
    lastChecked: now
  };

  return schemaCache.tasks;
}

export async function getClientsSchemaInfo() {
  const now = Date.now();
  if (schemaCache.clients?.lastChecked && (now - schemaCache.clients.lastChecked) < SCHEMA_CACHE_DURATION) {
    return schemaCache.clients;
  }

  const columnCheck = await sql`
    SELECT column_name
    FROM information_schema.columns
    WHERE table_name = 'clients'
      AND column_name IN ('notes', 'description', 'status', 'is_archived')
  `;

  schemaCache.clients = {
    hasNotes: columnCheck.some((row: any) => row.column_name === 'notes'),
    hasDescription: columnCheck.some((row: any) => row.column_name === 'description'),
    hasStatus: columnCheck.some((row: any) => row.column_name === 'status'),
    hasIsArchived: columnCheck.some((row: any) => row.column_name === 'is_archived'),
    lastChecked: now
  };

  return schemaCache.clients;
}

export function resetTasksSchemaCache() {
  schemaCache.tasks = undefined;
}

export type TasksSchemaInfo = Awaited<ReturnType<typeof getTasksSchemaInfo>>;
export type ClientsSchemaInfo = Awaited<ReturnType<typeof getClientsSchemaInfo>>;
